import { BlogPost } from '../../blog/types';

const post: BlogPost = {
  id: '10x-explained',
  title: 'Speed Makes Quality Cheap',
  excerpt:
    'When you move exponentially faster than everyone else, quality climbs and cost collapses. More cycles mean sharper work with fewer people.',
  category: 'AI & Technology',
  date: '2024-08-20',
  readTime: '5 min read',
  imageUrl: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&w=1600&q=80',
  author: {
    name: 'James Vogel',
    imageUrl: '/images/blog/james.png',
  },
  slug: '10x-explained',
  featured: true,
  content: [
    {
      type: 'impactStatement',
      content:
        'The day we started shipping 10x faster, we stopped arguing about the iron triangle. Quality went up and cost went down because speed gave us the time back.'
    },
    {
      type: 'heading2',
      content: 'The Moment It Clicked',
      description: 'Speed is leverage, not a bragging right.',
      id: 'moment-it-clicked'
    },
    {
      type: 'paragraph',
      content:
        "We hit that point where our release cadence outran every schedule around us. Instead of 'finishing early,' we used the extra cycles to hunt down edge cases, polish UX, and tighten copy. Quality didn't drop at speed--it finally had room to breathe."
    },
    {
      type: 'keyBulletList',
      content: {
        heading: 'What Exponential Speed Unlocks',
        description: 'Each additional iteration is a chance to upgrade the work, not ship faster for the sake of it.',
        points: [
          {
            icon: 'replay_circle_filled',
            title: 'More shots on goal',
            text: 'With ten extra reps before launch, the rough edges disappear by default.'
          },
          {
            icon: 'science',
            title: 'Live-fire QA',
            text: 'Bugs die in the same sprint they appear because we can loop fixes immediately.'
          },
          {
            icon: 'brush',
            title: 'Polish becomes standard',
            text: 'Micro-interactions, content tone, performance budgets--all get passes they never would at linear speed.'
          },
          {
            icon: 'description',
            title: 'Documentation by default',
            text: 'We document while we ship because the window for it actually exists.'
          }
        ]
      }
    },
    {
      type: 'impactStatement',
      content: 'Speed bought us the one resource that always makes things better: more attempts.'
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Speed Drives Quality Up',
      description: 'Iteration is the new craftsmanship.',
      id: 'speed-drives-quality'
    },
    {
      type: 'markdown',
      content: `Linear teams get one crack at a feature before launch. At 10x speed we get ten.

- Version 1: prove it works
- Version 2-4: fix the weird edge cases
- Version 5-7: tighten performance and UX
- Version 8-10: polish copy, refactor, add affordances

Quality isn't a heroic effort--it's the inevitable outcome of more deliberate passes.`
    },
    {
      type: 'quote',
      content: 'Craftsmanship is iteration at speed. If you get ten more swings, you will always out-polish someone stuck on their first draft.'
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'Speed Drives Cost Down',
      description: 'Same output, fewer people, less burn.',
      id: 'speed-drives-cost'
    },
    {
      type: 'paragraph',
      content:
        'Once the cycle time collapsed, labor stopped scaling linearly. Two operators now cover the surface area that used to require ten because automation and agents handle the busywork between decisions.'
    },
    {
      type: 'comparisonTable',
      content: {
        headers: ['Metric', 'Traditional Team', 'Our Strike Team'],
        rows: [
          ['People on the project', '10 specialists', '2 operators'],
          ['Weekly releases', '1 (maybe)', '5-7'],
          ['Features polished pre-launch', '20%', '90%+'],
          ['Hours spent on QA', 'Separate team', 'Inline with build'],
          ['Cost per shipped feature', 'Baseline 1.0x', '0.18x']
        ]
      }
    },
    {
      type: 'impactStatement',
      content: "Speed isn't about moving fast for the demo. It's how two people do the work of ten without breaking a sweat."
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: 'How We Operate With Two People',
      description: 'Systems > heroics.',
      id: 'operate-with-two'
    },
    {
      type: 'keyBulletList',
      content: {
        heading: 'Strike-Team Mechanics',
        description: 'The pattern we run every week to keep speed, quality, and cost aligned.',
        points: [
          {
            icon: 'group_work',
            title: 'Pods own the loop',
            text: 'Strategy, build, QA, and rollout stay inside the same pod so nothing waits in a queue.'
          },
          {
            icon: 'auto_mode',
            title: 'Agent scaffolding',
            text: 'Multi-agent flows prep research, tests, docs, and migrations so humans focus on decisions and polish.'
          },
          {
            icon: 'speed',
            title: 'Release rhythm',
            text: 'Every sprint ends with production. We adjust next sprint scope based on real telemetry, not gut feel.'
          },
          {
            icon: 'analytics',
            title: 'Telemetry-first',
            text: 'Usage data streams back within hours, giving us the signal to use our extra cycles where quality needs it most.'
          }
        ]
      }
    },
    {
      type: 'divider'
    },
    {
      type: 'heading2',
      content: "Why Most Teams Can't Catch Up",
      description: "Speed compounds; linear plans don't.",
      id: 'cant-catch-up'
    },
    {
      type: 'markdown',
      content: `Traditional teams:
- Treat speed as a deadline crunch instead of a systems upgrade
- Separate build, QA, and polish into different departments (which kills iteration)
- Measure hours, not outcomes, so cost is tied to headcount forever

Predator teams:
- Collapse the loop so ideas ship fast, then keep shipping until they shine
- Use automation to clear the busywork so small teams can attack bigger problems
- Let data steer every extra cycle toward quality instead of bureaucracy`
    },
    {
      type: 'impactStatement',
      content: 'Speed is the lever. Quality and cost swing with it. Once you feel that, you stop treating 10x as marketing and start treating it as the only way to work.'
    }
  ]
};

export default post;
